import CustomInput from '@/components/common/CustomInput';
import CustomSelect from '@/components/common/CustomSelect';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import useCreateRole from '@/hooks/RolesAndPriviledges/useCreateRole';
import { cn } from '@/utils';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface RoleFormField {
  label: string;
  type: string;
  name: string;
  placeholder: string;
  disable?: boolean;
}

const accessOptions = [
  { id: 1, label: 'No Access', value: false },
  { id: 2, label: 'Access', value: true },
];

const permissionOptions = [
  { id: 1, label: 'Disable', value: false },
  { id: 2, label: 'Enable', value: true },
];

const CreateRoles = () => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    onSubmit,
    rolesData,
    privilegesLoading,
    isLoading,
    isViewMode,
    editLoading,
    isEditMode,
    watch,
    handleUniversalToggle,
  } = useCreateRole();

  const fields: RoleFormField[] = [
    {
      label: 'Role Name',
      type: 'text',
      name: 'name',
      placeholder: 'Enter Role Name',
      disable: isViewMode,
    },
    {
      label: 'Description',
      type: 'text',
      name: 'description',
      placeholder: 'Enter description',
      disable: isViewMode,
    },
  ];

  const isSubmitting = isLoading || editLoading;

  return (
    <div className="p-3 sm:p-4">
      <div className="rounded-lg border border-green bg-light-green p-3 shadow-green-shadow sm:p-4">
        {/* Header */}
        <div className="mb-4 sm:mb-6 flex items-center gap-3 sm:gap-4 pb-3 sm:pb-4 border-b-2 border-green3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 bg-gray text-white rounded-lg transition-colors hover:opacity-90 shrink-0"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-textcolor text-lg sm:text-2xl font-bold truncate">
            {isEditMode ? 'Edit Role' : isViewMode ? 'View Role' : 'Create Role'}
          </h1>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="border border-gray/20 rounded-lg p-3 sm:p-4 flex flex-col gap-6"
        >
          {/* Role Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            {fields.map((field) => (
              <CustomInput
                key={field.name}
                control={control}
                name={field.name}
                type={field.type}
                label={field.label}
                placeholder={field.placeholder}
                inputClassName="pr-10 text-grayish-blue !border-gray !bg-green/10 w-full"
                labelClassName="text-textcolor font-medium"
                disabled={field.disable}
              />
            ))}
          </div>

          {/* Module Permissions */}
          <div>
            <div className="text-textcolor flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h1 className="font-semibold text-base sm:text-lg">Module Permissions:</h1>
              <div className="flex items-center gap-3">
                <span className="text-sm">Enable All</span>
                <Switch
                  disabled={isViewMode}
                  checked={watch('enableAll')}
                  onCheckedChange={(checked) => handleUniversalToggle(checked)}
                />
              </div>
            </div>

            {privilegesLoading ? (
              <div className="flex items-center justify-center gap-2 text-textcolor py-12">
                <Loader2 className="animate-spin" size={20} />
                <span className="text-sm">Loading permissions...</span>
              </div>
            ) : !rolesData?.length ? (
              <div className="flex items-center justify-center text-sm text-grayish-blue py-12">
                No modules found.
              </div>
            ) : (
              rolesData.map((module, moduleIndex) => {
                const accessKeys = Object.keys(module.access || {});
                return (
                  <div
                    key={module.id}
                    className="border border-gray/20 rounded-lg mb-4 overflow-hidden"
                  >
                    {/* Module Header */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center w-full justify-between bg-green/10 p-3">
                      <div className="w-full">
                        <h1 className="font-semibold text-textcolor truncate">
                          {module.permissions}
                        </h1>
                        <p className="text-sm text-black">{module.description}</p>
                      </div>

                      {/* Access Dropdown */}
                      <div className="w-full sm:w-56 shrink-0">
                        <CustomSelect
                          disable={isViewMode}
                          control={control}
                          name={`privilegesWithAccess.${moduleIndex}.hasAccess`}
                          options={accessOptions}
                          selectClassName="!bg-white"
                        />
                      </div>
                    </div>

                    {/* Dynamic Permissions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 p-3 sm:p-4">
                      {accessKeys.map((permissionKey) => (
                        <div
                          key={permissionKey}
                          className={cn(
                            'flex items-center justify-between gap-2 border rounded-md px-3 py-2',
                            'border-gray/20'
                          )}
                        >
                          <div className="capitalize text-textcolor text-sm truncate">
                            {permissionKey}
                          </div>

                          <div className="w-28 sm:w-36 shrink-0">
                            <CustomSelect
                              control={control}
                              disable={isViewMode}
                              name={`privilegesWithAccess.${moduleIndex}.access.${permissionKey}`}
                              options={permissionOptions}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!isViewMode && (
            <div className="flex justify-end pt-2 border-t border-gray/20">
              <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-40">
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="animate-spin" size={18} />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </span>
                ) : isEditMode ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateRoles;
